import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import axiosInstance from "../config/axiosConfig";
import { SUPPORT_ACCESS_MODULES, SUPPORT_ACCESS_FEATURE_PRESET } from "../pages/settings/Employees";

const SupportRequestList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedModules, setSelectedModules] = useState({});
  const [durationMinutes, setDurationMinutes] = useState(20);

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedEmailFromQuery = query.get("email") || "";
  const selectedRequestIdFromQuery = query.get("requestId") || "";

  const pendingRequests = useMemo(
    () => requests.filter((request) => request.status === "pending" || !request.status),
    [requests]
  );

  const selectedRequest = useMemo(() => {
    if (selectedRequestIdFromQuery) {
      return (
        pendingRequests.find((request) => {
          const id = String(
            request?.recordId || request?.requestId || request?._id || request?.id || ""
          );
          return id === String(selectedRequestIdFromQuery);
        }) || null
      );
    }

    if (!selectedEmailFromQuery) return null;
    const normalized = selectedEmailFromQuery.trim().toLowerCase();
    return (
      pendingRequests.find((request) => String(request.email || "").trim().toLowerCase() === normalized) ||
      null
    );
  }, [pendingRequests, selectedEmailFromQuery, selectedRequestIdFromQuery]);

  const isSingleRequestMode = Boolean(selectedEmailFromQuery || selectedRequestIdFromQuery);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/support/requests");
      setRequests(Array.isArray(response?.data) ? response.data : response?.data?.requests || []);
    } catch (error) {
      if (!selectedEmailFromQuery) {
        enqueueSnackbar(error?.response?.data?.message || "Unable to fetch support requests.", {
          variant: "error",
        });
      }
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, selectedEmailFromQuery]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const toggleModule = (requestId, moduleName) => {
    setSelectedModules((prev) => {
      const current = prev[requestId] || [];
      const next = current.includes(moduleName)
        ? current.filter((module) => module !== moduleName)
        : [...current, moduleName];
      return { ...prev, [requestId]: next };
    });
  };

  const approveRequest = async (request) => {
    const key =
      request?.recordId ||
      request?.requestId ||
      request?._id ||
      request?.id ||
      selectedRequestIdFromQuery ||
      request?.email;
    const modules = selectedModules[key] || [];
    const requestId =
      request?.recordId ||
      request?.requestId ||
      request?._id ||
      request?.id ||
      selectedRequestIdFromQuery;
    const duration = Number(durationMinutes);

    if (!requestId) {
      enqueueSnackbar("requestId is required to approve support access.", { variant: "error" });
      return;
    }
    if (!modules.length) {
      enqueueSnackbar("Select at least one module before approving.", { variant: "warning" });
      return;
    }
    if (!duration || duration <= 0) {
      enqueueSnackbar("Please enter a valid session duration in minutes.", { variant: "warning" });
      return;
    }
    try {
      await axiosInstance.post("/support/approve", {
        requestId,
        modules,
        duration,
      });
      enqueueSnackbar("Support request approved.", { variant: "success" });
      loadRequests();
      navigate("/dashboard/support-requests", { replace: true });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Approve failed.", { variant: "error" });
    }
  };

  const rejectRequest = async (request) => {
    const requestId =
      request?.recordId ||
      request?.requestId ||
      request?._id ||
      request?.id ||
      selectedRequestIdFromQuery;
    const email = request?.email || selectedEmailFromQuery;
    if (!requestId) {
      enqueueSnackbar("requestId is required to decline support access.", { variant: "error" });
      return;
    }
    try {
      await axiosInstance.post("/support/reject", {
        requestId,
        email,
      });
      enqueueSnackbar("Support request rejected.", { variant: "info" });
      loadRequests();
      navigate("/dashboard/support-requests", { replace: true });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Reject failed.", { variant: "error" });
    }
  };

  const extendSession = async (request) => {
    try {
      await axiosInstance.post("/support/extend-session", {
        supportUserId: request.supportUserId || request.userId || request._id,
        email: request.email,
      });
      enqueueSnackbar("Support session extended.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Extend session failed.", { variant: "error" });
    }
  };

  return (
    <div className="p-4 min-h-screen bg-slate-100">
      <div className="max-w-4xl mx-auto bg-white border rounded-md p-4 shadow-sm">
        <h2 className="text-xl font-semibold mb-4">Support Request List</h2>
        {loading ? <p className="text-sm text-gray-500">Loading requests...</p> : null}

        {/* If opened from notification: show the approval "block" for the requested email */}
        {isSingleRequestMode ? (
          <div className="mb-5 border rounded-md p-4 bg-slate-50">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-gray-500">Support Email</p>
                <p className="font-medium break-all">
                  {selectedRequest?.email || selectedEmailFromQuery || "Unknown support user"}
                </p>
              </div>
              <button
                onClick={() => navigate("/dashboard/support-requests", { replace: true })}
                className="text-sm text-blue-700 underline"
              >
                Back to list
              </button>
            </div>

            <div className="mt-4">
              <p className="text-sm font-semibold mb-2">Access Feature List</p>
              <div className="space-y-3">
                {(SUPPORT_ACCESS_MODULES || []).map((moduleGroup) => (
                  <div key={moduleGroup.id} className="border rounded-md p-3 bg-white">
                    <p className="text-sm font-semibold mb-2">{moduleGroup.name}</p>
                    <div className="flex flex-wrap gap-3">
                      {(moduleGroup.subModules || []).map((feature) => {
                        const requestId =
                          selectedRequest?.recordId ||
                          selectedRequest?.requestId ||
                          selectedRequest?._id ||
                          selectedRequest?.id ||
                          selectedRequestIdFromQuery ||
                          selectedEmailFromQuery;
                        const modules = selectedModules[requestId] || [];
                        const label = feature?.name;
                        const moduleValue = feature?.mappedFeature || label;
                        return (
                          <label key={feature.id} className="text-sm flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={modules.includes(moduleValue)}
                              onChange={() => toggleModule(requestId, moduleValue)}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 max-w-[240px]">
              <label className="text-sm font-semibold mb-1 block">Session Duration (minutes)</label>
              <input
                type="number"
                min={1}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() =>
                  approveRequest(
                    selectedRequest || {
                      email: selectedEmailFromQuery,
                    }
                  )
                }
                className="px-3 py-1 rounded bg-green-600 text-white text-sm"
              >
                Approve
              </button>
              <button
                onClick={() =>
                  rejectRequest(
                    selectedRequest || {
                      email: selectedEmailFromQuery,
                    }
                  )
                }
                className="px-3 py-1 rounded bg-red-600 text-white text-sm"
              >
                Decline
              </button>
            </div>
          </div>
        ) : null}

        {!isSingleRequestMode && !loading && !pendingRequests.length ? (
          <p className="text-sm text-gray-500">No pending support requests.</p>
        ) : null}
        {!isSingleRequestMode ? <div className="space-y-4">{pendingRequests.map((request) => {
            const requestId = request.recordId || request.requestId || request._id || request.id;
            const modules = selectedModules[requestId] || [];
            return (
              <div key={requestId} className="border rounded-md p-3">
                <p className="font-medium">{request.email || "No email"}</p>
                <p className="text-xs text-gray-500 mb-2">
                  Requested at: {request.createdAt ? new Date(request.createdAt).toLocaleString() : "-"}
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(SUPPORT_ACCESS_FEATURE_PRESET || []).map((module) => (
                    <label key={`${requestId}-${module.id}`} className="text-sm flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={modules.includes(module.mappedFeature || module.name)}
                        onChange={() => toggleModule(requestId, module.mappedFeature || module.name)}
                      />
                      {module.name}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => approveRequest(request)}
                    className="px-3 py-1 rounded bg-green-600 text-white text-sm"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => rejectRequest(request)}
                    className="px-3 py-1 rounded bg-red-600 text-white text-sm"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => extendSession(request)}
                    className="px-3 py-1 rounded border border-blue-600 text-blue-700 text-sm"
                  >
                    Extend Session
                  </button>
                </div>
              </div>
            );
          })}</div> : null}
      </div>
    </div>
  );
};

export default SupportRequestList;

